import * as React from 'react';
import styled from '@emotion/styled';
import CheckboxTree from 'react-checkbox-tree';
import TreeCollapseIcon from '../icons/TreeCollapseIcon';
import TreeExpandIcon from '../icons/TreeExpandIcon';
import TreeNonRunningVMIcon from '../icons/TreeNonRunningVMIcon';
import TreeCBCheckedIcon from '../icons/TreeCBCheckedIcon';
import TreeCBEmptyIcon from '../icons/TreeCBEmptyIcon';
import TreeCBPartialIcon from '../icons/TreeCBPartialIcon';
import TreeEyeIcon from '../icons/TreeEyeIcon';
import TreeExpandAllIcon from '../icons/TreeExpandAllIcon';
import TreeCollapseAllIcon from '../icons/TreeCollapseAllIcon';
import TreeSearchIcon from '../icons/TreeSearchIcon';
import TreeDatacenterIcon from '../icons/TreeDatacenterIcon';
import TreeClusterIcon from '../icons/TreeClusterIcon';
import TreeHypervisorIcon from '../icons/TreeHypervisorIcon';
import TreeVMIcon from '../icons/TreeVMIcon';
import TreeVMPausedIcon from '../icons/TreeVMPausedIcon';
import TreeVMNonRunningIcon from '../icons/TreeVMNonRunningIcon';
import './../css/Tree.css';
import { treeProjectModelDto } from '../models/dto/treeProjectModelDto';
import { treeNodeDto } from '../models/dto/treeNodeDto';
import { virtualTreeDataViewModel } from '../models/views/virtualTreeDataViewModel';
import TreeNodeTransformer from '../models/transformers/treeNodeTransformer';
import { TreeNodeStatus } from './TreeNodeStatus';

interface State {
  filteredNodes: virtualTreeDataViewModel[];
  checked: [];
  expanded: [];
  filterValue: string;
}
interface StylePropsCollapse {
  display: boolean;
}
const CollapseIcon = styled.label`
  margin-left: 1rem;
  display: ${(s: StylePropsCollapse) => (s.display ? 'inline-block' : 'none')};
`;
const ExpandIcon = styled.label`
  margin-left: 1rem;
  display: ${(s: StylePropsCollapse) => (s.display ? 'inline-block' : 'none')};
`;
const FilterContainer = styled.div`
  margin-top: 1rem;
  position: relative;
  border: 1px solid #ddd;
`;
const FilterInput = styled.input`
  width: calc(100% - 40px);
  line-height: 4rem;
  padding: 0 0 0 40px;
  border: 0;
`;
const ProjectTitle = styled.label`
  line-height: 2rem;
  background-color: #7ab800;
  color: white;
  width: 90%;
  padding: 5px 5%;
  display: block;
  margin-bottom: 5px;
`;

class Tree extends React.Component {
  state = {
    filteredNodes: [],
    checked: [],
    expanded: [],
    filterValue: '',
    showTree: true
  } as State;

  /**
   * Get array of labels of all checked nodes to pass into tree component
   */
  getCheckedChildren = (currentNodes: virtualTreeDataViewModel[]) => {
    let checkedNodes: string[] = currentNodes.map(node => {
      if (node.children.length > 0) {
        return this.getCheckedChildren(node.children);
      }

      if (node.currentCheckedState === TreeNodeStatus.UNCHECKED) {
        return '';
      }

      return node.label;
    });

    return checkedNodes;
  };

  flattenArray = (arr1: any) => {
    return arr1.reduce(
      (acc: any, val: any) =>
        Array.isArray(val) ? acc.concat(this.flattenArray(val)) : acc.concat(val),
      []
    );
  };

  async componentDidMount() {
    var id = (window as any).Dpack.Project.Id;
    const response = await fetch(`/api/opticalprime/${id}/virtualtree`);

    if (response.ok) {
      const json = (await response.json()) as treeProjectModelDto;
      const treeNodes = TreeNodeTransformer.from(json.treeNodes);

      const getCheckedNodes = this.getCheckedChildren(treeNodes);
      const checkedNodes = this.flattenArray(getCheckedNodes);

      this.setState({
        filteredNodes: treeNodes,
        checked: checkedNodes
      });
    }
  }

  hasChildren = (node: virtualTreeDataViewModel) => {
    return node.children && node.children.length > 0;
  };

  filterData = (currentNodes: virtualTreeDataViewModel[]) => {
    let filteredNodes: virtualTreeDataViewModel[] = currentNodes.filter(node => {
      if (node.children) {
        node.children = this.filterData(node.children);
      }
      if (this.hasChildren(node)) {
        return node;
      } else {
        return node.value
          .toString()
          .toLowerCase()
          .includes(this.state.filterValue.toLowerCase());
      }
    });
    return filteredNodes;
  };

  handleInputChange = (event: any) => {
    this.setState({ filterValue: event.target.value }, () => {
      const unFilteredNodes = this.state.filteredNodes;
      const updatedNodes =
        this.state.filterValue === '' ? unFilteredNodes : this.filterData(unFilteredNodes);
      this.setState({ filteredNodes: updatedNodes });
    });
  };

  getParentNodes = (nodes = this.state.filteredNodes) => {
    var ParentNodes: treeNodeDto[] = [];
    nodes.map(node => {
      if (node.children) {
        ParentNodes = ParentNodes.concat(this.getParentNodes(node.children));
        ParentNodes.push(node.value);
      }
    });
    return ParentNodes;
  };

  handleExpandClick = () => {
    let expanded = this.getParentNodes();
    this.setState({ expanded });
  };

  handleCollapseClick = () => {
    this.setState({ expanded: [] });
  };

  render() {
    return (
      <>
        <ExpandIcon
          display={this.state.expanded.length ? false : true}
          onClick={() => this.handleExpandClick()}
        >
          <TreeExpandAllIcon />
        </ExpandIcon>
        <CollapseIcon
          display={this.state.expanded.length ? true : false}
          onClick={() => this.handleCollapseClick()}
        >
          <TreeCollapseAllIcon />
        </CollapseIcon>
        <label>
          <TreeEyeIcon />
        </label>
        <label>
          <TreeNonRunningVMIcon />
        </label>
        <FilterContainer>
          <FilterInput
            placeholder="Filter VMs by name"
            value={this.state.filterValue}
            onChange={() => this.handleInputChange(event)}
          />
          <TreeSearchIcon />
        </FilterContainer>

        <ProjectTitle>Project</ProjectTitle>
        <CheckboxTree
          nodes={this.state.filteredNodes}
          checked={this.state.checked}
          expanded={this.state.expanded}
          onCheck={checked => {
            return this.setState({ checked });
          }}
          onExpand={expanded => {
            return this.setState({ expanded });
          }}
          icons={{
            check: <TreeCBCheckedIcon />,
            uncheck: <TreeCBEmptyIcon />,
            halfCheck: <TreeCBPartialIcon />,
            expandClose: <TreeCollapseIcon />,
            expandOpen: <TreeExpandIcon />,
            parentClose: <TreeVMIcon />,
            parentOpen: <TreeVMIcon />,
            leaf: <TreeNonRunningVMIcon />
          }}
        />
      </>
    );
  }
}
export default Tree;
